/* eslint-disable */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, CheckCircle2, DollarSign, Trash2, FileText, AlertOctagon, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { adminApi } from "@/lib/adminApi";
import { Card } from "@/components/admin/_shared";

const TIER_OPTIONS = ["free", "solo", "growth", "professional", "agency"];

export default function AdminUsersTab() {
  const [sub, setSub] = useState("users");
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [neverAudited, setNeverAudited] = useState(false);
  const [unverifiedOnly, setUnverifiedOnly] = useState(false);
  const [resolveModal, setResolveModal] = useState(null); // { dispute, action }
  const [selectedIds, setSelectedIds] = useState(new Set()); // bulk-select
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [termsVersion, setTermsVersion] = useState("1.0");
  const [termsPending, setTermsPending] = useState(0);
  const [termsUpdatedAt, setTermsUpdatedAt] = useState(null);
  const [termsUpdatedBy, setTermsUpdatedBy] = useState(null);
  const [bumpOpen, setBumpOpen] = useState(false);
  const [bumpInput, setBumpInput] = useState("");
  const [bumpBusy, setBumpBusy] = useState(false);
  // Item 5 — admin notes modal state
  const [notesModal, setNotesModal] = useState(null); // { user, notes, draft, busy }
  // Item 10 — invalidate modal + history modal state
  const [invModal, setInvModal] = useState(null); // { user, reason, busy }
  const [historyModal, setHistoryModal] = useState(null); // { user, entries }

  const loadTerms = async () => {
    try {
      const { data } = await adminApi.get("/admin/terms/version");
      setTermsVersion(data.current_version || "1.0");
      setTermsPending(data.pending_acceptance_count ?? 0);
      setTermsUpdatedAt(data.updated_at || null);
      setTermsUpdatedBy(data.updated_by || null);
    } catch {
      /* leave defaults */
    }
  };

  useEffect(() => {
    loadTerms();
  }, []);

  const bumpTermsVersion = async () => {
    const value = bumpInput.trim();
    if (!value) {
      toast.error("Enter a new version string (e.g. 1.1).");
      return;
    }
    if (value === termsVersion) {
      toast.error("That is already the active version.");
      return;
    }
    setBumpBusy(true);
    try {
      await adminApi.post("/admin/terms/version", { value });
      toast.success(`Active Terms version is now v${value}. All users will be re-prompted.`);
      setBumpOpen(false);
      setBumpInput("");
      await loadTerms();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to bump version.");
    } finally {
      setBumpBusy(false);
    }
  };

  const load = async () => {
    const u = await adminApi.get("/admin/users");
    setUsers(u.data.users || []);
    const f = await adminApi.get("/admin/feedback");
    setFeedback(f.data.feedback || []);
    const d = await adminApi.get("/admin/disputes");
    setDisputes(d.data.disputes || []);
  };

  const loadRevenue = async () => {
    const r = await adminApi.get("/admin/revenue/dashboard");
    setRevenue(r.data);
  };

  useEffect(() => { load().catch(() => {}); }, []);
  useEffect(() => { if (sub === "revenue") loadRevenue().catch(() => {}); }, [sub]);

  const setTier = async (uid, t) => {
    await adminApi.post(`/admin/users/${uid}/set-tier?tier=${t}`);
    toast.success("Tier updated");
    await load();
  };
  const resetQuota = async (u) => {
    if (!window.confirm(`Reset audit quota for ${u.email}?\n\nThis sets fresh_audits_this_month back to 0 and clears last_fresh_audit_date so the 30-day window restarts on their next audit.`)) return;
    try {
      const { data } = await adminApi.post(`/admin/users/${u.id}/reset-audit-quota`);
      const prev = data.previous || {};
      toast.success(
        `Reset ${u.email}: was ${prev.fresh_audits_this_month ?? 0} audit(s) / last ${(prev.last_fresh_audit_date || "never").slice(0, 10)}`,
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };
  // Item 10 — open the "reason required" modal instead of invalidating directly.
  const openInvalidate = (user) => {
    setInvModal({ user, reason: "", busy: false });
  };
  const submitInvalidate = async () => {
    if (!invModal) return;
    const reason = (invModal.reason || "").trim();
    if (!reason) {
      toast.error("Reason is required.");
      return;
    }
    setInvModal((m) => ({ ...m, busy: true }));
    try {
      await adminApi.post(`/admin/users/${invModal.user.id}/invalidate-cache`, { reason });
      toast.success("Score invalidated and recorded in user history.");
      setInvModal(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to invalidate");
      setInvModal((m) => ({ ...m, busy: false }));
    }
  };
  // Item 10 — open full invalidation history modal for the user
  const openHistory = async (user) => {
    try {
      const { data } = await adminApi.get(`/admin/users/${user.id}/invalidation-history`);
      setHistoryModal({ user, entries: data.invalidation_history || [] });
    } catch {
      toast.error("Could not load history");
    }
  };
  // Task 3 (2026-06-25) — Purge test/dummy accounts (destructive)
  const [purgeModal, setPurgeModal] = useState(null); // { candidates, busy }
  const openPurge = async () => {
    setPurgeModal({ candidates: [], busy: true, loading: true });
    try {
      const { data } = await adminApi.get("/admin/users/test-accounts/preview");
      setPurgeModal({
        candidates: data.candidates || [],
        totalBefore: data.total_users_before,
        preserved: data.preserved_emails || [],
        busy: false,
        loading: false,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to preview purge");
      setPurgeModal(null);
    }
  };
  const runPurge = async () => {
    setPurgeModal((m) => ({ ...m, busy: true }));
    try {
      const { data } = await adminApi.post("/admin/users/test-accounts/purge");
      toast.success(
        `Purged ${data.deleted} test account${data.deleted === 1 ? "" : "s"}. ` +
        `${data.preserved.length} preserved accounts intact. ` +
        `Total users: ${data.total_users_after}.`,
      );
      setPurgeModal(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
      setPurgeModal((m) => ({ ...m, busy: false }));
    }
  };
  // webhook failed to auto-resolve the user.
  const [linkSubModal, setLinkSubModal] = useState(null); // { user, subId, busy }
  const openLinkSub = (user) => setLinkSubModal({ user, subId: "", busy: false });
  const submitLinkSub = async () => {
    if (!linkSubModal) return;
    const sid = (linkSubModal.subId || "").trim();
    if (!sid) {
      toast.error("Lemon Squeezy subscription_id is required.");
      return;
    }
    setLinkSubModal((m) => ({ ...m, busy: true }));
    try {
      const { data } = await adminApi.post(
        `/admin/users/${linkSubModal.user.id}/link-subscription`,
        { subscription_id: sid },
      );
      toast.success(`Linked — user now on ${data.tier}. Renews ${(data.renews_at || "").slice(0, 10)}.`);
      setLinkSubModal(null);
      await load();
    } catch (e) {
      // Avoid leaking proxy HTML error pages (e.g. Cloudflare 502 body) into
      // the toast — only use `detail` when it's a string from our backend.
      const data = e?.response?.data;
      const detail = typeof data === "object" && data?.detail ? data.detail : null;
      toast.error(detail || "Failed to link subscription — check the LS subscription_id and try again.");
      setLinkSubModal((m) => ({ ...m, busy: false }));
    }
  };
  const openNotes = async (user) => {
    setNotesModal({ user, notes: [], draft: "", busy: true });
    try {
      const { data } = await adminApi.get(`/admin/users/${user.id}/notes`);
      setNotesModal({ user, notes: data.admin_notes || [], draft: "", busy: false });
    } catch {
      toast.error("Could not load notes");
      setNotesModal(null);
    }
  };
  const submitNote = async () => {
    if (!notesModal) return;
    const text = (notesModal.draft || "").trim();
    if (!text) {
      toast.error("Note text is required.");
      return;
    }
    setNotesModal((m) => ({ ...m, busy: true }));
    try {
      const { data } = await adminApi.post(`/admin/users/${notesModal.user.id}/notes`, { text });
      // Append-only — never overwrite previous notes (Constraint 2).
      setNotesModal((m) => ({
        ...m,
        notes: [...m.notes, data.entry],
        draft: "",
        busy: false,
      }));
      toast.success("Note saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save note");
      setNotesModal((m) => ({ ...m, busy: false }));
    }
  };
  const suspend = async (uid) => {
    if (!confirm("Suspend this account? They will be immediately blocked from logging in.")) return;
    try {
      await adminApi.post(`/admin/users/${uid}/suspend`);
      toast.success("Account suspended — login blocked.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to suspend");
    }
  };
  const unsuspend = async (uid) => {
    try {
      await adminApi.post(`/admin/users/${uid}/unsuspend`);
      toast.success("Account un-suspended.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const deleteOne = async (uid, email) => {
    if (!confirm(`Permanently DELETE ${email}? This wipes their merchant profile, audit history, disputes, and feedback. Cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/users/${uid}`);
      toast.success("User deleted.");
      setSelectedIds((s) => {
        const next = new Set(s);
        next.delete(uid);
        return next;
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };
  const bulkDelete = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (!confirm(`Permanently DELETE ${ids.length} user${ids.length === 1 ? "" : "s"}? Admin accounts will be skipped automatically. Cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await adminApi.post(`/admin/users/bulk-delete`, { user_ids: ids });
      toast.success(`Deleted ${data.deleted_count} user${data.deleted_count === 1 ? "" : "s"}${data.skipped_admin_emails?.length ? ` (skipped ${data.skipped_admin_emails.length} admin)` : ""}.`);
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };
  const toggleSelected = (uid) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };
  const toggleBypass = async (uid, enabled) => {
    try {
      await adminApi.post(`/admin/users/${uid}/bypass-verification`, { enabled });
      toast.success(enabled ? "Verification bypass enabled" : "Verification bypass disabled");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const toggleShareBadge = async (uid, disabled) => {
    try {
      await adminApi.post(`/admin/users/${uid}/share-badge`, { disabled });
      toast.success(disabled ? "Share badge disabled for user" : "Share badge re-enabled");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const updateFeedback = async (id, status) => {
    try {
      await adminApi.post(`/admin/feedback/${id}/status`, { admin_status: status });
      toast.success(`Marked ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const resolveDispute = async (note) => {
    if (!resolveModal) return;
    const { dispute, action } = resolveModal;
    const path = action === "confirm" ? "confirm-correct" : "mark-corrected";
    try {
      await adminApi.post(`/admin/disputes/${dispute.id}/${path}`, { admin_note: note });
      toast.success(action === "confirm" ? "Marked as system-correct" : "Marked as corrected");
      setResolveModal(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const wasAudited = (u) =>
    !!(u.merchant_profile?.composite_score != null || u.merchant_profile?.cached_result_json);

  // Item 6 — tier counts for the filter dropdown
  const tierCounts = TIER_OPTIONS.reduce((acc, t) => {
    acc[t] = users.filter((u) => u.tier === t).length;
    return acc;
  }, {});

  const visibleUsers = users
    .filter((u) => !search || u.email.toLowerCase().includes(search.toLowerCase()))
    .filter((u) => !neverAudited || !wasAudited(u))
    .filter((u) => tierFilter === "all" || u.tier === tierFilter)
    .filter((u) => !unverifiedOnly || u.merchant_profile?.store_url_unverified === true);

  return (
    <div className="space-y-4" data-testid="admin-users-tab">
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" onClick={() => setSub("users")} className={sub === "users" ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)]" : "bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)]"} data-testid="admin-users-sub-users">Users</Button>
        <Button size="sm" onClick={() => setSub("feedback")} className={sub === "feedback" ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)]" : "bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)]"} data-testid="admin-users-sub-feedback">Feedback ({feedback.length})</Button>
        <Button size="sm" onClick={() => setSub("disputes")} className={sub === "disputes" ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)]" : "bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)]"} data-testid="admin-users-sub-disputes">
          Disputes ({disputes.filter((d) => d.status === "open").length})
        </Button>
        <Button size="sm" onClick={() => setSub("revenue")} className={sub === "revenue" ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)]" : "bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)]"} data-testid="admin-users-sub-revenue">
          <DollarSign className="w-3.5 h-3.5 mr-1" /> Revenue
        </Button>
      </div>

      {sub === "users" && (
        <>
          <div
            className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] px-4 py-3 flex flex-wrap items-center gap-4"
            data-testid="admin-terms-version-card"
          >
            <div className="flex flex-col">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)] font-mono">
                Active Terms Version
              </span>
              <span className="text-[var(--rg-text-strong)] font-bold text-lg" data-testid="admin-terms-current-version">
                v{termsVersion}
              </span>
              {termsUpdatedAt && (
                <span className="text-[10px] text-[var(--rg-muted)] font-mono">
                  Updated {(termsUpdatedAt || "").slice(0, 10)}
                  {termsUpdatedBy ? ` · by ${termsUpdatedBy}` : ""}
                </span>
              )}
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)] font-mono">
                Pending Re-acceptance
              </span>
              <span
                className={`font-bold text-lg ${termsPending > 0 ? "text-[var(--rg-amber)]" : "text-[var(--rg-sage)]"}`}
                data-testid="admin-terms-pending-count"
              >
                {termsPending}
              </span>
            </div>
            <Button
              size="sm"
              onClick={() => {
                setBumpInput("");
                setBumpOpen(true);
              }}
              className="ml-auto bg-[var(--rg-amber)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-amber)] font-semibold"
              data-testid="admin-terms-bump-btn"
            >
              Bump Version
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by email…" className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] max-w-md" data-testid="users-search" />

            {/* Item 6 — tier filter dropdown */}
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] text-sm rounded h-9 px-2"
              data-testid="users-tier-filter"
            >
              <option value="all">{`All Tiers (${users.length})`}</option>
              {TIER_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {`${t.charAt(0).toUpperCase() + t.slice(1)} (${tierCounts[t] || 0})`}
                </option>
              ))}
            </select>

            <Button
              size="sm"
              onClick={() => setNeverAudited((v) => !v)}
              className={neverAudited ? "bg-[var(--rg-amber)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-amber)] font-semibold" : "bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface-2)]"}
              data-testid="users-never-audited-filter"
            >
              Never Audited {neverAudited ? "✓" : ""}
            </Button>

            {/* Tier-3 unverified-URL filter (Prompt I, Item 7) */}
            <Button
              size="sm"
              onClick={() => setUnverifiedOnly((v) => !v)}
              className={unverifiedOnly ? "bg-[var(--rg-amber)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-amber)] font-semibold" : "bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface-2)]"}
              data-testid="users-unverified-url-filter"
              title="Accounts flagged Tier-3 by automated store-URL verification."
            >
              Unverified Store URL{" "}
              {(() => {
                const n = users.filter((u) => u.merchant_profile?.store_url_unverified === true).length;
                return n > 0 ? `(${n})` : "";
              })()}
              {unverifiedOnly ? " ✓" : ""}
            </Button>

            {/* Task 3 — one-shot purge of test/dummy accounts */}
            <Button
              size="sm"
              onClick={openPurge}
              className="ml-auto bg-[var(--rg-red)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-red)] font-semibold"
              data-testid="users-purge-test-accounts"
              title="Delete test/dummy accounts (@example.com, referral.test.*, test_iter*, etc.) — preserves admin@regulens.com, prashant.modwel002+test1@gmail.com, getregulens@gmail.com."
            >
              Purge Test Accounts
            </Button>
            {selectedIds.size > 0 && (
              <Button
                size="sm"
                onClick={bulkDelete}
                disabled={bulkDeleting}
                className="bg-[var(--rg-red)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-red)] font-semibold"
                data-testid="users-bulk-delete-btn"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                {bulkDeleting ? "Deleting…" : `Delete Selected (${selectedIds.size})`}
              </Button>
            )}
            <span className="text-xs text-[var(--rg-muted)] ml-auto">{visibleUsers.length} / {users.length}</span>
          </div>
          <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)] border-b border-[var(--rg-border)]">
                  <th className="py-2 px-3 w-8">
                    <Checkbox
                      checked={
                        visibleUsers.length > 0 &&
                        visibleUsers.filter((u) => !u.is_admin).every((u) => selectedIds.has(u.id))
                      }
                      onCheckedChange={(v) => {
                        setSelectedIds((s) => {
                          const next = new Set(s);
                          visibleUsers.forEach((u) => {
                            if (u.is_admin) return;
                            if (v) next.add(u.id); else next.delete(u.id);
                          });
                          return next;
                        });
                      }}
                      data-testid="users-select-all"
                    />
                  </th>
                  <th className="py-2 px-3">Email</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3">Tier</th>
                  <th className="py-2 px-3">Store</th>
                  <th className="py-2 px-3">Score</th>
                  <th className="py-2 px-3">Terms Accepted</th>
                  <th className="py-2 px-3">Bypass Verify</th>
                  <th className="py-2 px-3">Share Badge</th>
                  <th className="py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleUsers.map((u) => (
                  <tr
                    key={u.id}
                    className={`border-b border-[var(--rg-border)]/60 ${u.is_suspended ? "bg-[var(--rg-red-soft)]" : ""}`}
                    data-testid={`user-row-${u.id}`}
                  >
                    <td className="py-2 px-3">
                      {!u.is_admin && (
                        <Checkbox
                          checked={selectedIds.has(u.id)}
                          onCheckedChange={() => toggleSelected(u.id)}
                          data-testid={`user-select-${u.id}`}
                        />
                      )}
                    </td>
                    <td className="py-2 px-3 text-[var(--rg-text-strong)]">{u.email} {u.is_admin && <span className="text-[10px] text-[var(--rg-red)] font-bold ml-1">ADMIN</span>}</td>
                    <td className="py-2 px-3">
                      {u.is_suspended ? (
                        <span
                          className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-[var(--rg-text-strong)] bg-[var(--rg-red)]"
                          data-testid={`user-status-suspended-${u.id}`}
                          title={u.suspended_at ? `Suspended ${u.suspended_at}` : "Suspended"}
                        >
                          Suspended
                        </span>
                      ) : u.is_verified ? (
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-[var(--rg-on-sage)] bg-[var(--rg-sage)]">
                          Active
                        </span>
                      ) : (
                        <span
                          className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-[var(--rg-on-sage)] bg-[var(--rg-amber)]"
                          data-testid={`user-status-store-unverified-${u.id}`}
                        >
                          Store Unverified
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-[var(--rg-text)] font-mono uppercase">{u.tier}</td>
                    <td className="py-2 px-3 text-[var(--rg-text)] font-mono text-xs">{u.merchant_profile?.shopify_store_url || "—"}</td>
                    <td className="py-2 px-3 text-[var(--rg-text)] font-mono">{u.merchant_profile?.composite_score ?? <span className="text-[var(--rg-muted)]">—</span>}</td>
                    <td className="py-2 px-3" data-testid={`user-terms-${u.id}`}>
                      <TermsCell user={u} currentVersion={termsVersion} />
                    </td>
                    <td className="py-2 px-3">
                      <Switch
                        checked={!!u.merchant_profile?.verification_bypass}
                        onCheckedChange={(v) => toggleBypass(u.id, v)}
                        data-testid={`user-bypass-${u.id}`}
                      />
                    </td>
                    <td className="py-2 px-3">
                      <Switch
                        checked={!u.merchant_profile?.share_badge_disabled}
                        onCheckedChange={(v) => toggleShareBadge(u.id, !v)}
                        data-testid={`user-share-badge-${u.id}`}
                      />
                    </td>
                    <td className="py-2 px-3 flex gap-1.5 flex-wrap">
                      {u.is_admin ? (
                        <span className="text-xs text-[var(--rg-muted)] font-mono italic h-7 flex items-center px-2">
                          admin · n/a
                        </span>
                      ) : (
                        <select className="bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] text-xs h-7 rounded px-2" value={u.tier} onChange={(e) => setTier(u.id, e.target.value)} data-testid={`user-tier-${u.id}`}>
                          {TIER_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      )}
                      {/* Item 5 — Notes icon (private admin notes per user) */}
                      {!u.is_admin && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openNotes(u)}
                          className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)] text-xs h-7 px-2"
                          title={`Private admin notes (${(u.admin_notes || []).length})`}
                          data-testid={`user-notes-${u.id}`}
                        >
                          <FileText className="w-3 h-3" />
                          {(u.admin_notes || []).length > 0 && (
                            <span className="ml-1 font-bold text-[var(--rg-amber)]">
                              {(u.admin_notes || []).length}
                            </span>
                          )}
                        </Button>
                      )}
                      {/* Item 10 — Invalidated badge (history exists) */}
                      {(u.invalidation_history || []).length > 0 && (
                        <button
                          type="button"
                          onClick={() => openHistory(u)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-[var(--rg-text-strong)] bg-[var(--rg-red)] hover:bg-[var(--rg-red)] h-7"
                          title={(() => {
                            const last = u.invalidation_history[u.invalidation_history.length - 1];
                            return last ? `${last.reason} — ${(last.timestamp || "").slice(0, 19)}` : "Invalidation history";
                          })()}
                          data-testid={`user-invalidated-badge-${u.id}`}
                        >
                          <AlertOctagon className="w-3 h-3" /> Invalidated
                          <span className="ml-1 font-mono">{(u.invalidation_history || []).length}</span>
                        </button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openInvalidate(u)} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)] text-xs h-7" data-testid={`user-invalidate-${u.id}`}>Invalidate</Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resetQuota(u)}
                        className="border-[var(--rg-sage)] text-[var(--rg-sage)] hover:bg-[var(--rg-sage)]/10 text-xs h-7"
                        title="Reset fresh_audits_this_month to 0 and clear last_fresh_audit_date so the 30-day rolling quota window restarts."
                        data-testid={`user-reset-quota-${u.id}`}
                      >
                        Reset Quota
                      </Button>
                      {/* Bug 1 — manually link an LS subscription when webhook missed */}
                      {!u.is_admin && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openLinkSub(u)}
                          className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)] text-xs h-7"
                          title="Manually link a Lemon Squeezy subscription_id when the webhook didn't auto-resolve the user."
                          data-testid={`user-link-sub-${u.id}`}
                        >
                          Link LS Sub
                        </Button>
                      )}
                      {/* Tier-3 cleanup action (Prompt I, Item 7) */}
                      {u.merchant_profile?.store_url_unverified === true && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            try {
                              await adminApi.post(`/admin/users/${u.id}/clear-store-unverified`);
                              toast.success("Unverified flag cleared");
                              await load();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Failed");
                            }
                          }}
                          className="border-[var(--rg-amber)] text-[var(--rg-amber)] hover:bg-[var(--rg-amber-soft)] text-xs h-7"
                          data-testid={`user-clear-unverified-${u.id}`}
                          title="Mark this Tier-3 unverified store as legitimate"
                        >
                          Clear Unverified
                        </Button>
                      )}
                      {!u.is_admin && (
                        u.is_suspended ? (
                          <Button size="sm" variant="outline" onClick={() => unsuspend(u.id)} className="border-[var(--rg-sage)] text-[var(--rg-sage)] hover:bg-[var(--rg-sage-soft)] text-xs h-7" data-testid={`user-unsuspend-${u.id}`}>Unsuspend</Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => suspend(u.id)} className="border-[var(--rg-red)] text-[var(--rg-red)] hover:bg-[var(--rg-red-soft)] text-xs h-7" data-testid={`user-suspend-${u.id}`}>Suspend</Button>
                        )
                      )}
                      {!u.is_admin && (
                        <Button size="sm" variant="outline" onClick={() => deleteOne(u.id, u.email)} className="border-[var(--rg-red)] text-[var(--rg-red)] hover:bg-[var(--rg-red-soft)] text-xs h-7" data-testid={`user-delete-${u.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {visibleUsers.length === 0 && (
                  <tr><td colSpan={10} className="py-6 text-center text-[var(--rg-muted)]">No users match the current filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {sub === "feedback" && (
        <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)] border-b border-[var(--rg-border)]">
                <th className="py-2 px-3">From</th>
                <th className="py-2 px-3">★</th>
                <th className="py-2 px-3">Comment</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {feedback.map((f) => (
                <tr key={f.id} className="border-b border-[var(--rg-border)]/60" data-testid={`feedback-row-${f.id}`}>
                  <td className="py-2 px-3 text-[var(--rg-text-strong)]">{f.user_email}</td>
                  <td className="py-2 px-3 text-[var(--rg-amber)] font-mono">{"★".repeat(f.rating)}{f.rating <= 2 && <span className="text-[var(--rg-red)] ml-1 text-[10px] font-bold">ALERT</span>}</td>
                  <td className="py-2 px-3 text-[var(--rg-text)] max-w-md break-words">{f.body || <span className="text-[var(--rg-muted)] italic">no comment</span>}</td>
                  <td className="py-2 px-3 font-mono text-xs uppercase">{f.admin_status}</td>
                  <td className="py-2 px-3 flex gap-1.5 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => updateFeedback(f.id, "contacted")} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] text-xs h-7">Reviewed</Button>
                    <Button size="sm" variant="outline" disabled={f.rating < 3} onClick={() => updateFeedback(f.id, "approved_for_testimonial")} className="border-[var(--rg-sage)] text-[var(--rg-sage)] hover:bg-[var(--rg-sage-soft)] text-xs h-7 disabled:opacity-40" data-testid={`feedback-approve-${f.id}`}>Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => updateFeedback(f.id, "declined")} className="border-[var(--rg-red)] text-[var(--rg-red)] text-xs h-7">Decline</Button>
                  </td>
                </tr>
              ))}
              {feedback.length === 0 && (<tr><td colSpan={5} className="py-6 text-center text-[var(--rg-muted)]">No feedback yet.</td></tr>)}
            </tbody>
          </table>
        </div>
      )}

      {sub === "disputes" && (
        <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] overflow-x-auto" data-testid="admin-disputes-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)] border-b border-[var(--rg-border)]">
                <th className="py-2 px-3">From</th>
                <th className="py-2 px-3">Juris</th>
                <th className="py-2 px-3">Finding</th>
                <th className="py-2 px-3">Reason</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {disputes.map((d) => (
                <tr key={d.id} className="border-b border-[var(--rg-border)]/60" data-testid={`dispute-row-${d.id}`}>
                  <td className="py-2 px-3 text-[var(--rg-text-strong)] text-xs">{d.user_email}</td>
                  <td className="py-2 px-3 text-[var(--rg-text)] font-mono uppercase">{d.jurisdiction || "—"}</td>
                  <td className="py-2 px-3 text-[var(--rg-text)] max-w-sm break-words text-xs">{d.finding_text}</td>
                  <td className="py-2 px-3 text-[var(--rg-text)] max-w-sm break-words text-xs">{d.dispute_reason || <span className="text-[var(--rg-muted)] italic">none</span>}</td>
                  <td className="py-2 px-3 font-mono text-xs uppercase">
                    {d.status === "open" && <span className="text-[var(--rg-amber)]">open</span>}
                    {d.status === "confirmed_correct" && <span className="text-[var(--rg-sage)]">system correct</span>}
                    {d.status === "marked_corrected" && <span className="text-[var(--rg-sage)]">corrected</span>}
                  </td>
                  <td className="py-2 px-3 flex gap-1.5 flex-wrap">
                    {d.status === "open" ? (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setResolveModal({ dispute: d, action: "confirm" })} className="border-[var(--rg-sage)] text-[var(--rg-sage)] hover:bg-[var(--rg-sage-soft)] text-xs h-7" data-testid={`dispute-confirm-${d.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Confirm Correct
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setResolveModal({ dispute: d, action: "correct" })} className="border-[var(--rg-sage)] text-[var(--rg-sage)] hover:bg-[var(--rg-sage-soft)] text-xs h-7" data-testid={`dispute-correct-${d.id}`}>
                          Mark Corrected
                        </Button>
                      </>
                    ) : (
                      <span className="text-[10px] text-[var(--rg-muted)] font-mono">{d.admin_email}</span>
                    )}
                  </td>
                </tr>
              ))}
              {disputes.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-[var(--rg-muted)]">No disputed findings yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {sub === "revenue" && (
        <div className="space-y-4" data-testid="admin-revenue-tab">
          {!revenue ? (
            <div className="text-[var(--rg-text)]">Loading revenue…</div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card label="MRR" value={`$${revenue.mrr_usd.toFixed(2)}`} valueColor="var(--rg-sage)" testId="rev-mrr" />
                <Card label="ARR" value={`$${revenue.arr_usd.toFixed(2)}`} valueColor="var(--rg-sage)" />
                <Card label="Paying subscriptions" value={revenue.paying_subscriptions} testId="rev-subs" />
                <Card label="Signups this month" value={revenue.signups_this_month} testId="rev-signups" />
                <Card label="New signups (30d)" value={revenue.new_signups_30d} />
                <Card label="Churn (30d)" value={revenue.churn_30d} valueColor="var(--rg-red)" testId="rev-churn" />
                <Card label="Churn rate" value={`${revenue.churn_rate_pct}%`} valueColor={revenue.churn_rate_pct >= 5 ? "var(--rg-red)" : "var(--rg-sage)"} />
                <Card label="As of" value={revenue.as_of?.slice(0, 16).replace("T", " ")} />
              </div>
              <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4">
                <div className="font-heading font-bold text-[var(--rg-text-strong)] mb-3">Tier breakdown</div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                  {Object.entries(revenue.tier_breakdown).map(([t, count]) => (
                    <div key={t} className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3 text-center" data-testid={`rev-tier-${t}`}>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)] font-bold">{t}</div>
                      <div className="font-heading font-extrabold text-xl mt-1 text-[var(--rg-text-strong)]">{count}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ---- Dispute resolution modal ---- */}
      <Dialog open={!!resolveModal} onOpenChange={(o) => !o && setResolveModal(null)}>
        <DialogContent className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]" data-testid="dispute-resolve-modal">
          <DialogHeader>
            <DialogTitle>
              {resolveModal?.action === "confirm" ? "Confirm finding is correct" : "Mark finding as corrected"}
            </DialogTitle>
          </DialogHeader>
          <DisputeResolveForm dispute={resolveModal?.dispute} onSubmit={resolveDispute} />
        </DialogContent>
      </Dialog>

      {/* ---- Bump Terms Version dialog ---- */}
      <Dialog open={bumpOpen} onOpenChange={(o) => !o && setBumpOpen(false)}>
        <DialogContent className="bg-[var(--rg-surface)] border-[var(--rg-amber)] text-[var(--rg-text-strong)] max-w-md" data-testid="admin-terms-bump-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[var(--rg-text-strong)]">Bump Terms Version</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-[var(--rg-text)]">
              Current version is <span className="text-[var(--rg-text-strong)] font-mono font-bold">v{termsVersion}</span>.
              Bumping forces every non-admin user to re-accept the Terms + Refund Policy on their next page load.
            </div>
            <Label className="text-[var(--rg-text)] text-xs uppercase tracking-wider">New version string</Label>
            <Input
              value={bumpInput}
              onChange={(e) => setBumpInput(e.target.value)}
              placeholder="e.g. 1.1"
              maxLength={32}
              className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] font-mono"
              data-testid="admin-terms-bump-input"
            />
            <div className="flex gap-2 justify-end pt-2">
              <Button
                onClick={() => setBumpOpen(false)}
                className="bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface-2)]"
                data-testid="admin-terms-bump-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={bumpTermsVersion}
                disabled={bumpBusy}
                className="bg-[var(--rg-amber)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-amber)] font-bold"
                data-testid="admin-terms-bump-confirm"
              >
                {bumpBusy ? "Bumping…" : "Confirm Bump"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Task 3 — Purge Test Accounts modal */}
      <Dialog open={!!purgeModal} onOpenChange={(o) => !o && !purgeModal?.busy && setPurgeModal(null)}>
        <DialogContent className="bg-[var(--rg-surface)] border-[var(--rg-red)] text-[var(--rg-text-strong)] max-w-2xl" data-testid="admin-purge-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[var(--rg-text-strong)]">
              Purge test / dummy accounts?
            </DialogTitle>
          </DialogHeader>
          {purgeModal?.loading ? (
            <div className="text-sm text-[var(--rg-text)]">Loading candidates…</div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm text-[var(--rg-text)]">
                About to delete <span className="font-bold text-[var(--rg-red)]" data-testid="admin-purge-count">{purgeModal?.candidates?.length ?? 0}</span> account{purgeModal?.candidates?.length === 1 ? "" : "s"} matching the test-account regex patterns.
                Their <span className="font-mono">merchant_profiles</span>, <span className="font-mono">audit_history</span>,
                and related rows will be cascade-deleted.
              </div>
              <div className="rounded-md border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] p-3 text-sm">
                <div className="font-bold text-[var(--rg-sage)] text-xs uppercase tracking-wider mb-1">
                  Preserved accounts (never deleted):
                </div>
                <ul className="font-mono text-[var(--rg-text)] text-xs space-y-0.5" data-testid="admin-purge-preserved">
                  {(purgeModal?.preserved || []).map((e) => (
                    <li key={e}>· {e}</li>
                  ))}
                </ul>
              </div>
              <div className="max-h-52 overflow-y-auto rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-2 font-mono text-[10px] text-[var(--rg-text)]" data-testid="admin-purge-candidates">
                {(purgeModal?.candidates || []).length === 0 ? (
                  <div className="italic text-[var(--rg-muted)]">No matches. Nothing to delete.</div>
                ) : (
                  purgeModal.candidates.map((c) => (
                    <div key={c.id}>{c.email}</div>
                  ))
                )}
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  onClick={() => setPurgeModal(null)}
                  className="bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface-2)]"
                  data-testid="admin-purge-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={runPurge}
                  disabled={purgeModal?.busy || (purgeModal?.candidates?.length ?? 0) === 0}
                  className="bg-[var(--rg-red)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-red)] font-bold"
                  data-testid="admin-purge-confirm"
                >
                  {purgeModal?.busy ? "Purging…" : `Delete ${purgeModal?.candidates?.length ?? 0} accounts`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bug 1 — Manual Link LS Subscription modal */}
      <Dialog open={!!linkSubModal} onOpenChange={(o) => !o && setLinkSubModal(null)}>
        <DialogContent className="bg-[var(--rg-surface)] border-[var(--rg-sage)] text-[var(--rg-text-strong)] max-w-md" data-testid="admin-link-sub-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[var(--rg-text-strong)]">Link Lemon Squeezy subscription</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-[var(--rg-text)]">
              For <span className="font-mono text-[var(--rg-text-strong)]">{linkSubModal?.user?.email}</span>.
              Enter the LS <span className="font-mono">subscription_id</span> (visible in
              the LS dashboard order) and we'll fetch the live tier + renewal date,
              attach it to this user, and log an admin_notes entry.
            </div>
            <Label className="text-[var(--rg-text)] text-xs uppercase tracking-wider">Subscription ID</Label>
            <Input
              value={linkSubModal?.subId || ""}
              onChange={(e) => setLinkSubModal((m) => ({ ...m, subId: e.target.value }))}
              placeholder="e.g. 12345678"
              className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] font-mono"
              data-testid="admin-link-sub-input"
            />
            <div className="flex justify-end gap-2">
              <Button
                onClick={() => setLinkSubModal(null)}
                className="bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface-2)]"
                data-testid="admin-link-sub-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={submitLinkSub}
                disabled={!linkSubModal?.subId?.trim() || linkSubModal?.busy}
                className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-bold"
                data-testid="admin-link-sub-confirm"
              >
                {linkSubModal?.busy ? "Linking…" : "Confirm Link"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Item 5 — Admin Notes modal */}
      <Dialog open={!!notesModal} onOpenChange={(o) => !o && setNotesModal(null)}>
        <DialogContent className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] max-w-2xl" data-testid="admin-notes-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[var(--rg-text-strong)]">
              Admin Notes · {notesModal?.user?.email}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-[var(--rg-text)]">
              Private. Visible only to admins. Notes are append-only — submissions
              append a new entry rather than overwriting existing notes.
            </div>
            <div
              className="max-h-72 overflow-y-auto rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3 space-y-2"
              data-testid="admin-notes-list"
            >
              {(notesModal?.notes || []).length === 0 ? (
                <div className="text-sm text-[var(--rg-muted)] italic">No notes yet.</div>
              ) : (
                notesModal.notes.map((n, i) => (
                  <div
                    key={i}
                    className="rounded border border-[var(--rg-border)] bg-[var(--rg-surface)] p-2.5 text-sm"
                    data-testid={`admin-note-entry-${i}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider text-[var(--rg-muted)] font-mono flex flex-wrap gap-2">
                      <span>{(n.timestamp || "").slice(0, 19)}</span>
                      <span className="text-[var(--rg-text)]">{n.admin_email}</span>
                    </div>
                    <div className="text-[var(--rg-text-strong)] mt-1 whitespace-pre-wrap">{n.text}</div>
                  </div>
                ))
              )}
            </div>
            <Label className="text-[var(--rg-text)] text-xs uppercase tracking-wider">Add a new note</Label>
            <Textarea
              value={notesModal?.draft || ""}
              onChange={(e) => setNotesModal((m) => ({ ...m, draft: e.target.value }))}
              rows={4}
              maxLength={4000}
              className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]"
              data-testid="admin-notes-input"
            />
            <div className="flex justify-end gap-2">
              <Button
                onClick={() => setNotesModal(null)}
                className="bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface-2)]"
                data-testid="admin-notes-close"
              >
                Close
              </Button>
              <Button
                onClick={submitNote}
                disabled={!notesModal?.draft?.trim() || notesModal?.busy}
                className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-bold"
                data-testid="admin-notes-save"
              >
                {notesModal?.busy ? "Saving…" : "Save Note"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Item 10 — Invalidate (reason required) modal */}
      <Dialog open={!!invModal} onOpenChange={(o) => !o && setInvModal(null)}>
        <DialogContent className="bg-[var(--rg-surface)] border-[var(--rg-red)] text-[var(--rg-text-strong)] max-w-md" data-testid="admin-invalidate-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[var(--rg-text-strong)]">Invalidate score</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-[var(--rg-text)]">
              You are about to invalidate the cached score for{" "}
              <span className="font-mono text-[var(--rg-text-strong)]">{invModal?.user?.email}</span>.
              This action is recorded in the user's invalidation history.
            </div>
            <Label className="text-[var(--rg-text)] text-xs uppercase tracking-wider">
              Reason for invalidation (required)
            </Label>
            <Textarea
              value={invModal?.reason || ""}
              onChange={(e) => setInvModal((m) => ({ ...m, reason: e.target.value }))}
              rows={4}
              placeholder="e.g. State rule changed; recompute score from scratch."
              className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]"
              data-testid="admin-invalidate-reason"
            />
            <div className="flex justify-end gap-2">
              <Button
                onClick={() => setInvModal(null)}
                className="bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface-2)]"
                data-testid="admin-invalidate-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={submitInvalidate}
                disabled={!invModal?.reason?.trim() || invModal?.busy}
                className="bg-[var(--rg-red)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-red)] font-bold"
                data-testid="admin-invalidate-confirm"
              >
                {invModal?.busy ? "Invalidating…" : "Confirm Invalidation"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Item 10 — Invalidation history modal */}
      <Dialog open={!!historyModal} onOpenChange={(o) => !o && setHistoryModal(null)}>
        <DialogContent className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] max-w-2xl" data-testid="admin-history-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[var(--rg-text-strong)] flex items-center gap-2">
              <History className="w-5 h-5" /> Invalidation History · {historyModal?.user?.email}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-2" data-testid="admin-history-list">
            {(historyModal?.entries || []).length === 0 ? (
              <div className="text-sm text-[var(--rg-muted)] italic">No invalidations recorded.</div>
            ) : (
              historyModal.entries
                .slice()
                .reverse()
                .map((e, i) => (
                  <div
                    key={i}
                    className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3 text-sm"
                    data-testid={`admin-history-entry-${i}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider text-[var(--rg-muted)] font-mono flex flex-wrap gap-2">
                      <span>{(e.timestamp || "").slice(0, 19)}</span>
                      <span className="text-[var(--rg-text)]">{e.invalidated_by}</span>
                    </div>
                    <div className="text-[var(--rg-text-strong)] mt-1 whitespace-pre-wrap">{e.reason}</div>
                  </div>
                ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DisputeResolveForm({ dispute, onSubmit }) {
  const [note, setNote] = useState("");
  if (!dispute) return null;
  return (
    <div className="space-y-3">
      <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3 text-xs text-[var(--rg-text)]">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)] mb-1">Finding</div>
        <div className="text-[var(--rg-text-strong)]">{dispute.finding_text}</div>
        {dispute.dispute_reason && (
          <>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)] mt-2 mb-1">User reason</div>
            <div>{dispute.dispute_reason}</div>
          </>
        )}
      </div>
      <Label className="text-[var(--rg-text)] text-xs uppercase">Admin note (optional)</Label>
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]" data-testid="dispute-admin-note" />
      <Button onClick={() => onSubmit(note)} className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" data-testid="dispute-resolve-submit">
        Submit
      </Button>
    </div>
  );
}


function TermsCell({ user, currentVersion }) {
  const accepted = user.terms_accepted_version;
  const acceptedAt = user.terms_accepted_at;
  if (!accepted) {
    return (
      <span
        className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-[var(--rg-text-strong)] bg-[var(--rg-muted)]"
        data-testid={`user-terms-blank-${user.id}`}
        title="No Terms acceptance recorded yet"
      >
        Not accepted
      </span>
    );
  }
  const stale = accepted !== currentVersion;
  const dateOnly = (acceptedAt || "").slice(0, 10);
  return (
    <div className="flex flex-col gap-0.5">
      <span
        className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider w-fit ${
          stale ? "text-[var(--rg-on-sage)] bg-[var(--rg-amber)]" : "text-[var(--rg-on-sage)] bg-[var(--rg-sage)]"
        }`}
        data-testid={stale ? `user-terms-stale-${user.id}` : `user-terms-current-${user.id}`}
        title={stale ? `Pending re-acceptance — current version is ${currentVersion}` : "On the active Terms version"}
      >
        v{accepted}{stale ? " · re-accept" : ""}
      </span>
      <span className="text-[10px] text-[var(--rg-muted)] font-mono">{dateOnly || "—"}</span>
    </div>
  );
}

